export const WoodMixSource=Object.freeze({
  CATALOGUE:'catalogue',
  INVENTORY:'inventory'
});

const SOURCES=new Set(Object.values(WoodMixSource));
const KEY=/^[a-z][a-z0-9_]{1,63}$/;
const EPSILON=0.01;

function required(value,label,max=160){
  const text=String(value||'').trim();
  if(!text)throw new TypeError(label+' is required');
  if(text.length>max)throw new TypeError(label+' is too long');
  return text;
}

function optionalStableKey(value,label){
  if(value==null||value==='')return null;
  const key=String(value).trim().toLowerCase();
  if(!KEY.test(key))throw new TypeError('Invalid '+label);
  return key;
}

function source(value){
  const key=String(value||'').trim().toLowerCase();
  if(!SOURCES.has(key))throw new TypeError('Unsupported wood mix source');
  return key;
}

function share(value){
  if(value==null||value==='')return null;
  const number=Number(value);
  if(!Number.isFinite(number)||number<=0||number>100){
    throw new TypeError('sharePercent must be greater than 0 and at most 100');
  }
  return number;
}

function order(value){
  const number=value==null||value===''?0:Number(value);
  if(!Number.isInteger(number)||number<0||number>1000000){
    throw new TypeError('sortOrder must be an integer between 0 and 1000000');
  }
  return number;
}

function timestamp(value,label){
  if(value==null||value==='')return null;
  const text=String(value).trim();
  if(Number.isNaN(Date.parse(text)))throw new TypeError(label+' must be an ISO timestamp');
  return text;
}

function idOrGenerated(value){
  const id=String(value||'').trim();
  if(id)return id;
  const generated=globalThis.crypto?.randomUUID?.();
  if(generated)return generated;
  throw new TypeError('id is required when crypto.randomUUID is unavailable');
}

export function createSessionWood({
  id,
  userId,
  sessionId,
  sourceKind,
  woodKey=null,
  inventoryItemId=null,
  nameSnapshot,
  sharePercent=null,
  sortOrder=0,
  deletedAt=null
}={}){
  const kind=source(sourceKind);
  const catalogueKey=optionalStableKey(woodKey,'woodKey');
  const inventoryId=inventoryItemId==null||inventoryItemId===''?null:required(inventoryItemId,'inventoryItemId',128);

  if(kind===WoodMixSource.CATALOGUE){
    if(!catalogueKey||inventoryId)throw new TypeError('Catalogue wood requires woodKey and no inventoryItemId');
  }else if(!inventoryId){
    throw new TypeError('Inventory wood requires inventoryItemId');
  }

  return Object.freeze({
    id:idOrGenerated(id),
    userId:required(userId,'userId',128),
    sessionId:required(sessionId,'sessionId',128),
    sourceKind:kind,
    woodKey:catalogueKey,
    inventoryItemId:inventoryId,
    nameSnapshot:required(nameSnapshot,'nameSnapshot',160),
    sharePercent:share(sharePercent),
    sortOrder:order(sortOrder),
    deletedAt:timestamp(deletedAt,'deletedAt')
  });
}

export function updateSessionWood(item,changes={}){
  const current=createSessionWood(item);
  return createSessionWood({
    ...current,
    ...changes,
    id:current.id,
    userId:current.userId,
    sessionId:current.sessionId
  });
}

export function removeSessionWood(item,{at=new Date().toISOString()}={}){
  return updateSessionWood(item,{deletedAt:timestamp(at,'at')});
}

export function orderSessionWoods(items,{includeDeleted=false}={}){
  if(!Array.isArray(items))throw new TypeError('wood mix items must be an array');
  return Object.freeze(
    items
      .map(createSessionWood)
      .filter(item=>includeDeleted||!item.deletedAt)
      .sort((a,b)=>a.sortOrder-b.sortOrder||String(a.id).localeCompare(String(b.id)))
  );
}

export function woodMixShareSummary(items=[]){
  const active=orderSessionWoods(items);
  const specified=active.filter(item=>item.sharePercent!=null);
  const total=specified.reduce((sum,item)=>sum+item.sharePercent,0);
  const allSpecified=active.length>0&&specified.length===active.length;
  const over=total>100+EPSILON;
  const exact=allSpecified&&Math.abs(total-100)<=EPSILON;
  const valid=!over&&(!allSpecified||exact);

  return Object.freeze({
    itemCount:active.length,
    specifiedCount:specified.length,
    totalPercent:Number(total.toFixed(4)),
    allSpecified,
    complete:exact,
    valid,
    status:active.length===0||specified.length===0
      ?'optional'
      :over||allSpecified&&!exact
        ?'invalid'
        :allSpecified
          ?'complete'
          :'partial'
  });
}

export function assertWoodMixPlanValid(items=[]){
  const summary=woodMixShareSummary(items);
  if(!summary.valid){
    throw new TypeError(
      summary.totalPercent>100
        ?'Wood share total cannot exceed 100%'
        :'When every wood has a share, the total must equal 100%'
    );
  }
  return summary;
}

export function sessionWoodToRow(item){
  const value=createSessionWood(item);
  return Object.freeze({
    id:value.id,
    user_id:value.userId,
    session_id:value.sessionId,
    source_kind:value.sourceKind,
    wood_key:value.woodKey,
    inventory_item_id:value.inventoryItemId,
    name_snapshot:value.nameSnapshot,
    share_percent:value.sharePercent,
    sort_order:value.sortOrder,
    deleted_at:value.deletedAt
  });
}

export function sessionWoodFromRow(row={}){
  return createSessionWood({
    id:row.id,
    userId:row.user_id,
    sessionId:row.session_id,
    sourceKind:row.source_kind,
    woodKey:row.wood_key,
    inventoryItemId:row.inventory_item_id,
    nameSnapshot:row.name_snapshot,
    sharePercent:row.share_percent,
    sortOrder:row.sort_order,
    deletedAt:row.deleted_at
  });
}
